import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, TextInput, TouchableOpacity, Text, FlatList, Pressable, Platform } from 'react-native';
import { FontAwesome5, MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { InteractiveSearchTypes } from '../Types';
import searchStyles from '../searchStyles';
import { Checkbox } from 'tamagui';
import parseByDelimiter from '@utils/parseByDelimiter';
import { debounce, set } from "lodash";
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import AnimatedLottieView from '@utils/lottieAnimation';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import LoadingIndicator from '@components/Global/Spinner';
import {
  useHasActiveSubscriptionWithContext,
  useIsFreePlanWithContext,
  useAIUsageStatsForFreePlan
} from '@utils/billingUtils';

const AnimatedIcon = ({ name, theme }) => (
  <Animatable.View useNativeDriver animation="fadeInDown" duration={250} style={[searchStyles.iconStyle, { overflow: "hidden" }]}>
    <FontAwesome5 name={name} size={name === 'ellipsis-h' ? 24 : 20} color={!theme ? "white" : "grey"} style={searchStyles.searchIconStyle} />
  </Animatable.View>
);


const InteractiveSearch = ({
  aiCallback,
  aiSearch,
  placeHolder,
  searchParams,
  callback,
  onFocus,
  onBlur,
  isExpanded = false,
  multiline = false,
  buttonColumnTop,
  customIconsCount = 0,
  value
}: InteractiveSearchTypes) => {
  const [focusedState, setFocusedState] = useState(false);
  const [text, setText] = useState(value || "");
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterArray, setFilterArray] = useState([]);
  const searchRef = useRef(null);
  const theme = themeSelector();
  const [AILoading, setAILoading] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const lastExternalValueRef = useRef<string | undefined>(value);

  // Sync internal text state with external value prop (only when value changes externally)
  useEffect(() => {
    if (value !== undefined && value !== lastExternalValueRef.current) {
      setText(value);
      lastExternalValueRef.current = value;
    }
  }, [value]); // Sync whenever value prop changes

  // Billing utilities for AI usage stats
  const hasActiveSubscription = useHasActiveSubscriptionWithContext();
  const isFreePlan = useIsFreePlanWithContext();
  const { data: aiUsageStats, isLoading: aiUsageLoading } = useAIUsageStatsForFreePlan();

  // Debug log for wand button state (only log when key props change, not on every render)
  useEffect(() => {
    if (aiSearch) {
      console.log('🔮 Wand button state:', {
        aiSearch,
        isExpanded,
        hasCallback: !!aiCallback,
        shouldRender: aiSearch && isExpanded
      });
    }
  }, [aiSearch, isExpanded]); // Only depend on aiSearch and isExpanded to avoid infinite loops

  const clearSearch = useCallback(() => {
    setText("");
    callback?.("");
    lastExternalValueRef.current = "";
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  }, [callback]);

  // Always use internal text state for display - it's synced with value prop when needed
  const displayValue = text;
  const hasValue = displayValue && displayValue.length > 0;

  const handler = useCallback((text: string) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      callback?.(text);
    }, 300);
  }, [callback]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const RowItem = ({ item, index }) => {
    const [checked, setChecked] = useState(false);

    const handleSelect = useCallback((checked, item) => {
      if (checked) {
        setFilterArray(value => [...value, item.dataTitle])
      }
    }, [])

    return (
      <View style={{ flexDirection: 'row' }}>
        <Checkbox status={checked ? "checked" : "unchecked"} onPress={() => { setChecked(!checked); handleSelect(checked) }} />
        <Text style={{ justifyContent: 'center', verticalAlign: 'middle' }}>{parseByDelimiter(item.dataTitle)}</Text>
      </View>
    )
  }

  useEffect(() => {
    if (filterArray.length > 0) {
      let newArray = [];
      searchParams.forEach(item => {
        if (filterArray.includes(item.id)) {
          newArray.push(item.dataSchema)
        }
      }
      )
    }
    return () => {
      setFilterArray([]);
    }
  }, [
    filterArray
  ])


  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center', top: Platform.OS === 'android' ? 0 : -3, flexBasis: 120, flexGrow: 20, maxHeight: Platform.OS === 'android' ? 48 : 40, borderRadius: 20, overflow: 'visible', position: 'relative' as const }}>
      {Platform.OS === 'web' ? <View style={{ flex: 1, maxWidth: 60, alignItems: 'center', justifyContent: 'center', alignContent: 'center', flexDirection: 'column', gap: 4 }}>
        {/* AI Usage Stats - above robot animation when expanded */}
        {isExpanded && aiSearch && (
          <View style={{
            width: 50,
            height: 50,
            borderRadius: 25,
            backgroundColor: theme ? '#f3f4f6' : '#374151',
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: theme ? '#e5e7eb' : '#4b5563',
            marginBottom: 4,
          }}>
            {hasActiveSubscription ? (
              <MaterialIcons
                name="all-inclusive"
                size={28}
                color={theme ? '#22c55e' : '#10b981'}
              />
            ) : aiUsageLoading ? (
              <LoadingIndicator
                type="indeterminate"
                color={theme ? '#3b82f6' : '#60a5fa'}
              />
            ) : (
              <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{
                  fontSize: 16,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  lineHeight: 18,
                }}>
                  {aiUsageStats?.currentCount || aiUsageStats?.thisMonth || 0}
                </Text>
                <Text style={{
                  fontSize: 10,
                  fontWeight: '500',
                  color: theme ? '#64748b' : '#94a3b8',
                  lineHeight: 12,
                }}>
                  /{aiUsageStats?.limit || 5}
                </Text>
              </View>
            )}
          </View>
        )}
        {focusedState ? <AnimatedIcon theme={theme} name="ellipsis-h" /> : (aiSearch ? <AnimatedLottieView source={require("@assets/animations/automation_robot.json")} autoPlay loop style={{ height: 50, width: 50 }} /> : <AnimatedIcon name="search" theme={theme} />)}
      </View>
        : <View style={{ minWidth: 10 }} />
      }
      {
        filterVisible ?
          <View style={{ position: 'absolute', minHeight: 300, top: 50, left: 100, backgroundColor: 'rgb(90,192,234)', borderRadius: 20, overflow: 'visible', zIndex: 20 }}>
            <FlatList data={searchParams} style={{ flex: 1 }} renderItem={({ item, index }) => { return (<RowItem item={item} index={index} />) }} />
          </View>
          : null
      }
      {
        filterArray.length > 0 &&
        <View style={{ flexDirection: 'column' }}>
          {
            filterArray.map((item, index) => {
              return (
                <View style={{ backgroundColor: 'rgb(90,192,234)' }}>
                  <Text>
                    {item}
                  </Text>
                </View>
              )
            })

          }
        </View>
      }
      <TextInput
        ref={searchRef}
        returnKeyLabel='Search'
        returnKeyType={multiline ? 'default' : 'search'}
        multiline={multiline}
        onFocus={() => {
          setFocusedState(true);
          onFocus?.();
        }}
        onBlur={() => {
          setFocusedState(false);
          onBlur?.();
        }}
        style={[searchStyles.inputStyle, { 
          flex: 1,
          flexShrink: 1,
          borderRadius: 20,
          maxWidth: 600,
          color: theme ? '#1f2937' : '#f9fafb',
          outlineStyle: 'none' as any, // Remove web outline
          alignSelf: 'center',
          paddingRight: isExpanded ? 60 : (aiSearch ? 110 : 60), // Less padding when expanded since buttons are in column
          minHeight: multiline ? 120 : (Platform.OS === 'android' ? 44 : undefined),
          maxHeight: multiline ? (isExpanded ? 300 : 150) : undefined,
          textAlignVertical: multiline ? 'top' as const : 'center' as const,
          paddingTop: multiline ? 12 : (Platform.OS === 'android' ? 12 : undefined),
          paddingBottom: multiline ? 12 : (Platform.OS === 'android' ? 12 : undefined),
          paddingVertical: multiline ? undefined : (Platform.OS === 'android' ? 12 : undefined),
        }]}
        value={displayValue}
        onChangeText={(newText) => {
          if (newText.includes('/')) {
            setFilterVisible(true);
          }
          setText(newText);
          handler(newText);
        }}
        placeholder={placeHolder || 'Search'}
        placeholderTextColor={theme ? "#9ca3af" : "#6b7280"}
        accessibilityLabel="Search input"
        accessibilityRole="search"
        blurOnSubmit={!multiline}
      />
      {/* Button container - horizontal when collapsed, vertical column when expanded */}
      {!isExpanded ? (
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          position: 'absolute' as const,
          right: 0,
          height: '100%',
          zIndex: 20,
          pointerEvents: 'box-none' as const,
          gap: aiSearch ? -12 : 0,
        }}>
          <Pressable
            style={{
              opacity: text !== '' ? 1 : 0.2,
              justifyContent: 'center',
              alignItems: 'center',
              alignContent: 'center',
              width: 50,
              height: 50,
              zIndex: 21,
              marginRight: aiSearch ? -8 : 0,
            }}
            disabled={!hasValue}
            hitSlop={{
              top: 10,
              bottom: 10,
              left: 10,
              right: 10,
            }}
            onPress={clearSearch}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            accessibilityState={{ disabled: !hasValue }}
          >
            <MaterialIcons
              name="cancel"
              size={24}
              style={{ overflow: 'visible', width: 50 }}
              color={hasValue ? (theme ? 'black' : 'white') : 'rgba(128,128,128,0.3)'}
            />
          </Pressable>
          {
            aiSearch &&
            <Pressable
              disabled={!hasValue || AILoading}
                onPress={async () => {
                if (!displayValue || !aiCallback) {
                  console.warn('⚠️ Wand button: Missing text or callback', { text: !!displayValue, callback: !!aiCallback });
                  return;
                }
                  try {
                  setAILoading(true);
                  await aiCallback(displayValue);
                } catch (err) {
                  console.error('❌ AI Search error:', err);
                } finally {
                  setAILoading(false);
                }
              }}
              style={{
                opacity: hasValue && !AILoading ? 1 : 0.2,
                width: 50,
                height: 50,
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                zIndex: 22,
                backgroundColor: 'transparent',
                marginLeft: -8,
              }}
              hitSlop={{
                top: 15,
                bottom: 15,
                left: 15,
                right: 15,
              }}
              accessibilityRole="button"
              accessibilityLabel="AI search"
              accessibilityState={{ disabled: !hasValue || AILoading }}
            >
              {
                AILoading ?
                  <LoadingIndicator type={undefined} color={"rgb(90,192,234)"} />
                  :
                  <FontAwesome name="magic" size={24} color={theme ? "black" : "white"} />
              }
            </Pressable>
          }
        </View>
      ) : (
          // When expanded, position buttons horizontally beside each other when AI callback is active
          // Otherwise, use vertical column layout
        <View style={{
            flexDirection: aiCallback ? 'row' as const : 'column' as const,
          alignItems: 'center' as const,
          position: 'absolute' as const,
          right: 12, // Match SearchBar's button column right position
          top: buttonColumnTop !== undefined ? buttonColumnTop : 20, // Use calculated top position from SearchBar
            gap: aiCallback ? 8 : 12, // Smaller gap when horizontal
          zIndex: 1000, // Higher z-index to ensure visibility
          pointerEvents: 'box-none' as const,
          elevation: 10, // Android elevation
        }}>
          {/* Clear button */}
          <Pressable
            style={{
              opacity: text !== '' ? 1 : 0.2,
              justifyContent: 'center',
              alignItems: 'center',
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: theme ? '#f5f5f5' : '#2a2a2a',
              zIndex: 1001,
            }}
            disabled={!hasValue}
            hitSlop={{
              top: 10,
              bottom: 10,
              left: 10,
              right: 10,
            }}
            onPress={clearSearch}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            accessibilityState={{ disabled: !hasValue }}
          >
            <MaterialIcons
              name="cancel"
              size={20}
              color={hasValue ? (theme ? 'black' : 'white') : 'rgba(128,128,128,0.3)'}
            />
          </Pressable>
          {/* Wand button - always render when aiSearch is true */}
          {aiSearch ? (
            <Pressable
              disabled={!hasValue || AILoading}
              onPress={async () => {
                console.log('✨ Wand button onPress triggered', { text: displayValue, AILoading, hasCallback: !!aiCallback });
                if (!displayValue || !aiCallback) {
                  console.warn('⚠️ Wand button: Missing text or callback', { text: !!displayValue, callback: !!aiCallback });
                  return;
                }
                try {
                  console.log('🚀 Starting AI callback with text:', text);
                  setAILoading(true);
                  await aiCallback(displayValue);
                  console.log('✅ AI callback completed successfully');
                } catch (err) {
                  console.error('❌ AI Search error:', err);
                } finally {
                  setAILoading(false);
                  console.log('🏁 AI callback finished, loading set to false');
                }
              }}
              style={{
                opacity: text !== '' && !AILoading ? 1 : 0.4, // Increased opacity when disabled so it's more visible
                width: 40,
                height: 40,
                borderRadius: 20,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: theme ? '#f5f5f5' : '#2a2a2a',
                zIndex: 1001,
                borderWidth: 1, // Add border to make it more visible
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}
              hitSlop={{
                top: 15,
                bottom: 15,
                left: 15,
                right: 15,
              }}
              accessibilityRole="button"
              accessibilityLabel="AI search"
              accessibilityState={{ disabled: !hasValue || AILoading }}
            >
              {
                AILoading ?
                  <LoadingIndicator type={undefined} color={"rgb(90,192,234)"} />
                  :
                  <FontAwesome name="magic" size={20} color={theme ? (text ? "black" : "#6b7280") : (text ? "white" : "#9ca3af")} />
              }
            </Pressable>
          ) : null}
        </View>
      )}
    </View>
  );
};

export default InteractiveSearch;
